import styled from 'styled-components';
import {
  ModalOverlay,
  ModalContent,
  InteractiveItem,
  ItemContent,
  } from './Common.styled';

// Language Modal Overlay
export const LanguageModalOverlay = styled(ModalOverlay)`
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(0, 0, 0, 0.7)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlur || 'blur(4px)'}; /* Reduced for TV performance */
`;

// Language Modal Content
export const LanguageModalContent = styled(ModalContent)`
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlur || 'blur(4px)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '20px'};
  max-width: 1200px;
  width: 95%;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
  
  /* Glow Effect - REMOVED for TV performance */
  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => props.theme.colors?.glowGradient || 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)'};
    border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '24px'} ${(props) => props.theme.effects?.borderRadiusLarge || '24px'} 0 0;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  } */
`;

// Language Header (unique to language modal)
export const LanguageHeader = styled.div`
  padding: 30px 40px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  
  h2 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    background: ${(props) => props.theme.colors?.textActive || 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)'};
    -webkit-background-clip: text;
    background-clip: text;
    /* text-shadow removed for TV performance */
  }
`;

// Language Grid (unique to language modal)
export const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 30px 40px;
  max-height: 60vh;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.3)'};
    border-radius: 4px;
    
    &:hover {
      background: ${(props) => props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.5)'};
    }
  }
`;

// Language Item (extends InteractiveItem with language-specific styling)
export const LanguageItem = styled(InteractiveItem)`
  height: 120px;
  padding: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  outline:none !important;
  
  /* Glass Overlay - REMOVED for TV performance */
  /* &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors?.glassBackground || 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), transparent)'};
    pointer-events: none;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  }
  
  &:hover::after {
    background: ${(props) => props.theme.colors?.glowGradient || 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent)'};
  } */
  
  ${({ disabled }) => disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
    
    &:hover {
      transform: none;
    }
  `}
`;

// Language Item Content (extends ItemContent with language-specific styling)
export const LanguageItemContent = styled(ItemContent)`
  gap: 12px;
  width: 100%;
`;

// Language Icon (unique to language modal)
export const LanguageIcon = styled.div`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  /* text-shadow removed for TV performance */
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  ${({ active }) => active && `
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* text-shadow removed for TV performance */
  `}
`;

// Language Name (unique text styling for language names)
export const LanguageName = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  /* text-shadow removed for TV performance */
  text-align: center;
  margin: 0;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
  
  ${({ active }) => active && `
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* text-shadow removed for TV performance */
    font-weight: 700;
  `}
`;

// Language Code (unique text styling for language codes)
export const LanguageCode = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'}CC;
  /* text-shadow removed for TV performance */
  text-align: center;
  margin: 0;
  opacity: 0.8;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
  
  ${({ active }) => active && `
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    opacity: 1;
  `}
`;

// Status Indicator (unique to language modal)
export const StatusIndicator = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) => props.active 
    ? props.theme.colors?.glowGradient || 'linear-gradient(135deg, #4ade80, #22c55e)'
    : props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.3)'};
  
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
  
  ${({ active }) => active && `
    animation: pulse 2s infinite;
  `}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`; 